import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { doc, getDocs, updateDoc } from 'firebase/firestore'
import { Formula } from '@otw/models'
import { db } from '@otw/models/db'
import { formulaConverter, formulaRuleConverter } from '@otw/models/converters'

interface UseFormulasWithRulesOptions {
  onSuccess?: () => void
  onError?: (error: Error) => void
}

interface UseFormulasWithRulesParams {
  options?: UseFormulasWithRulesOptions
}

export const useFormulasWithRules = ({ options = {} }: UseFormulasWithRulesParams) => {
  const queryKey = ['formulas-with-rules']

  const {
    data: formulas,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<Formula[]>({
    queryKey,
    queryFn: async () => {
      // First, fetch all formulas
      const formulasSnapshot = await getDocs(db.formulas.withConverter(formulaConverter))
      const formulas = formulasSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      }))

      // Then, fetch rules for each formula and attach them
      const formulasWithRules = await Promise.all(
        formulas.map(async (formula) => {
          try {
            const rulesSnapshot = await getDocs(
              db.getFormulaRules(formula.id).withConverter(formulaRuleConverter)
            )
            const rules = rulesSnapshot.docs.map(doc => ({
              ...doc.data(),
              id: doc.id,
            }))
            
            return {
              ...formula,
              rules: rules.filter(rule => rule.status === 'active').sort((a, b) => a.order - b.order)
            }
          } catch (error) {
            console.warn(`Failed to fetch rules for formula ${formula.id}:`, error)
            return {
              ...formula,
              rules: []
            }
          }
        })
      )

      return formulasWithRules
    },
    select: data => data.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const queryClient = useQueryClient()

  const updateFormulaMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Formula> }) => {
      const collectionWithConverter = db.formulas.withConverter(formulaConverter)
      const formulaRef = doc(collectionWithConverter, id)
      const updateData = formulaConverter.toFirestore({
        ...data,
        updatedAt: new Date().toISOString(),
      } as Formula)
      await updateDoc(formulaRef, updateData)
      return { id, ...updateData }
    },
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey })
      const previousFormulas = queryClient.getQueryData<Formula[]>(queryKey)
      queryClient.setQueryData<Formula[]>(queryKey, old =>
        old ? old.map(f => (f.id === id ? { ...f, ...data } : f)) : old
      )
      return { previousFormulas }
    },
    onError: (_err, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previousFormulas)
    },
  })

  return {
    formulas,
    isLoading,
    isError,
    error,
    refetchFormulas: refetch,
    updateFormula: updateFormulaMutation.mutateAsync,
  }
} 